import Button from '@atoms/Button/Button';
import ProfilePicture from '@atoms/ProfilePicture/ProfilePicture';
import { IAmbassadorQuote } from '@models/IAmbassadorQuote';
import ProfilePictureSizeEnum from '@models/enums/ProfilePictureSizeEnum';
import ImageLightBox from '@molecules/ImageLightBox/ImageLightBox';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';

interface Props extends IAmbassadorQuote {
  readonly contentIds: string[];
}

const AmbassadorQuotesCard = ({
  author,
  contentIds,
  image,
  link,
  quote,
  quoteAuthor,
  dataTestId = 'Ambassador-Quotes-Card',
}: Props) => {
  const { authorName, ambassador, specialism, subTitle, thumbnail } = author;
  // Only include subtitle if ambassador
  const filteredSpecialisms = [
    ...(ambassador ? [subTitle] : []),
    ...(specialism ?? []),
  ].filter(Boolean);

  return (
    <>
      <ImageLightBox
        imageClasses="h-full w-full object-center object-cover"
        contentIds={contentIds}
        lightboxIconBottom
        {...image}
        dataTestId={dataTestId}
      />
      <div
        className={classNames(
          ' pointer-events-none top-0 z-30 flex h-full w-full flex-col content-start justify-between gap-7 text-black-100 after:absolute after:bottom-0 after:h-[75%] after:w-full after:bg-gradient-to-t after:from-[rgba(0,0,0,0.7)] after:to-[rgba(0,0,0,0)] max-lg:after:hidden lg:absolute lg:after:from-[rgba(0,0,0,0.65)] xl:after:h-1/2 dark:text-white',
          {
            'before:absolute before:top-0 before:h-[25%] before:w-full before:bg-gradient-to-b before:from-[rgba(0,0,0,0.7)] before:to-[rgba(0,0,0,0)] max-lg:before:hidden':
              thumbnail || authorName || link?.name,
          }
        )}
      >
        <div
          className={classNames(
            'top-10 left-10 flex w-auto flex-col items-center lg:absolute lg:m-0 lg:flex-row lg:content-center',
            {
              '-mt-[38px]': thumbnail,
            }
          )}
        >
          {thumbnail && (
            <ProfilePicture
              size={ProfilePictureSizeEnum.Size76}
              thumbnail={thumbnail}
              displayCheck={ambassador}
              displayOutline={ambassador}
              dataTestId={`${dataTestId}-Profile-Picture`}
            />
          )}
          <div
            className={classNames(
              'body-two flex flex-col items-center gap-y-1 text-center lg:items-start lg:gap-0',
              thumbnail ? 'mt-5 lg:mt-0 lg:ml-5' : 'mt-9 lg:mt-0'
            )}
          >
            {authorName && <span className="lg:font-medium">{authorName}</span>}
            <div className="flex flex-row flex-wrap justify-center">
              {filteredSpecialisms.map((bullet, index) => (
                <span
                  key={bullet}
                  className={classNames('whitespace-nowrap', {
                    'pr-2': index !== filteredSpecialisms.length - 1,
                  })}
                >
                  {index !== 0 && <span className="pr-2 text-white">•</span>}
                  {bullet}
                </span>
              ))}
            </div>
          </div>
        </div>
        {link?.name && link?.url && (
          <Button
            className="pointer-events-auto top-10 right-10 order-last mt-auto self-center lg:absolute lg:order-none"
            link={link}
            linkCallback={() => {
              updateDataLayer({
                event: 'selectContent',
                content_type: 'ambassador_quote_read_article',
                link_text: link.url,
              });
            }}
            dataTestId={`${dataTestId}-CTA`}
          />
        )}
        <div
          className="z-10 flex w-full flex-col items-center justify-center gap-4 text-center lg:absolute lg:bottom-20 lg:gap-5 lg:px-22 xl:bottom-24 xl:px-64"
          data-testid={`${dataTestId}-Quote`}
        >
          {quote && (
            <blockquote className="h3">&ldquo;{`${quote}`}&rdquo;</blockquote>
          )}
          {quoteAuthor && (
            <span className="body-two text-yellow">{quoteAuthor}</span>
          )}
        </div>
      </div>
    </>
  );
};

export default AmbassadorQuotesCard;
